/**
 * [비즈니스 목적]
 * Hyperlounge Ontology 대시보드 - 메인 애플리케이션 컴포넌트
 * Google OAuth 인증 기반 경영정보 파일 온톨로지 관리 및 AI 변환 시스템
 *
 * [워크플로우]
 * 1. Google OAuth 로그인 (AdminLoginPage)
 * 2. 테넌트 선택 → Context에서 전역 상태 관리
 * 3. 메인 기능 접근:
 *    - 파일 온톨로지 관리 (OntologyPage)
 *    - 프롬프트 관리 (PromptManagementPage)
 *    - 배치 변환 실행 (HyperloungeConverterPage)
 *
 * [주요 기능]
 * - React Router 기반 SPA 라우팅
 * - Material-UI 테마 설정 (HyperLounge 브랜드 컬러 적용)
 * - Context Providers 계층 구조 (Auth → GlobalTenant → Customer)
 * - Protected Routes (인증 필요) / Public Routes (로그인 페이지)
 * - Toast 알림 시스템 (react-hot-toast)
 *
 * [아키텍처]
 * - ErrorBoundary로 전체 앱 감싸기
 * - ThemeProvider로 Material-UI 테마 주입
 * - AuthProvider → 인증 상태 관리
 * - Router → 라우팅 관리
 * - AppContent → 인증 상태에 따른 라우트 분기
 */

import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { Toaster } from 'react-hot-toast';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import { GlobalTenantProvider } from './contexts/GlobalTenantContext';
import Layout from './components/Layout/Layout';
import AdminLoginPage from './pages/Auth/AdminLoginPage';
import AdminManagementPage from './pages/AdminManagement/AdminManagementPage';
import LoadingScreen from './components/Common/LoadingScreen';
import TenantManagementPageOntology from './pages/TenantManagement/TenantManagementPageOntology';
import OntologyPage from './pages/Ontology/OntologyPage';
import PromptManagementPage from './pages/PromptManagement/PromptManagementPage';
import HyperloungeConverterPage from './pages/HyperloungeConverter/HyperloungeConverterPage';
import AgentMonitorPage from './pages/AgentMonitor/AgentMonitorPage';

const theme = createTheme({
  palette: {
    primary: {
      main: '#FF4444', // HyperLounge primary red (from website)
      light: '#FF6666',
      dark: '#CC0000',
    },
    secondary: {
      main: '#1a1a1a', // HyperLounge black
      light: '#333333',
      dark: '#000000',
    },
    error: {
      main: '#d32f2f',
      light: '#ef5350',
      dark: '#c62828',
    },
    background: {
      default: '#f5f5f5', // Light gray background for better data visibility
      paper: '#ffffff', // White paper for cards
    },
    text: {
      primary: '#333333', // Dark text for white background
      secondary: '#666666', // Medium gray for secondary text
    },
  },
  typography: {
    fontFamily: '"Spoqa Han Sans Neo", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 16, // Base font size for desktop readability
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      '@media (min-width:1200px)': {
        fontSize: '3rem',
      },
    },
    h2: {
      fontWeight: 700,
      fontSize: '2rem',
      '@media (min-width:1200px)': {
        fontSize: '2.5rem',
      },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      '@media (min-width:1200px)': {
        fontSize: '2rem',
      },
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      '@media (min-width:1200px)': {
        fontSize: '1.75rem',
      },
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      '@media (min-width:1200px)': {
        fontSize: '1.5rem',
      },
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      '@media (min-width:1200px)': {
        fontSize: '1.25rem',
      },
    },
    body1: {
      fontSize: '1rem',
      '@media (min-width:1200px)': {
        fontSize: '1.125rem',
      },
    },
    body2: {
      fontSize: '0.875rem',
      '@media (min-width:1200px)': {
        fontSize: '1rem',
      },
    },
  },
  shape: {
    borderRadius: 6, // HyperLounge border radius
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FF4444', // HyperLounge red app bar
          color: '#ffffff', // White text
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)', // Standard shadow
          // Desktop-optimized app bar
          '@media (min-width: 1200px)': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff', // White card background
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', // Subtle shadow
          color: '#333333', // Dark text for white cards
          // Enhanced shadows for desktop
          '@media (min-width: 1200px)': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8, // Slightly more rounded
          fontSize: '1rem',
          padding: '8px 16px',
          // Desktop-optimized buttons
          '@media (min-width: 1200px)': {
            fontSize: '1.125rem',
            padding: '12px 24px',
            borderRadius: 10,
          },
        },
        contained: {
          backgroundColor: '#FF4444',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(255,68,68,0.3)',
          '&:hover': {
            backgroundColor: '#CC0000',
            boxShadow: '0 4px 12px rgba(255,68,68,0.4)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: '#FF4444',
          color: '#FF4444',
          backgroundColor: 'white',
          '&:hover': {
            borderColor: '#CC0000',
            backgroundColor: 'rgba(255,68,68,0.08)',
          },
        },
        text: {
          color: '#333333',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', // Subtle shadow
          color: '#333333',
          borderRadius: 12, // More rounded corners
          // Enhanced cards for desktop
          '@media (min-width: 1200px)': {
            boxShadow: '0 4px 16px rgba(255,68,68,0.08)',
            borderRadius: 16,
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#f5f5f5',
          '& .MuiTableCell-head': {
            color: '#333333',
            fontWeight: 600,
            fontSize: '1rem',
            borderBottom: '2px solid #e0e0e0',
            '@media (min-width: 1200px)': {
              fontSize: '1.125rem',
            },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(0,0,0,0.02)',
          },
          '&:hover': {
            backgroundColor: 'rgba(255,68,68,0.08)',
          },
          '& .MuiTableCell-root': {
            color: '#333333',
            borderBottom: '1px solid #e0e0e0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: '#333333',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
            borderRadius: 8,
            color: '#333333',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.23)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255,68,68,0.5)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#FF4444',
            },
            '& input': {
              color: '#333333',
            },
          },
          '& .MuiInputLabel-root': {
            color: 'rgba(0,0,0,0.6)',
            '&.Mui-focused': {
              color: '#FF4444',
            },
          },
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: '#333333',
        },
        h1: { color: '#333333' },
        h2: { color: '#333333' },
        h3: { color: '#333333' },
        h4: { color: '#333333' },
        h5: { color: '#333333' },
        h6: { color: '#333333' },
        body1: { color: '#333333' },
        body2: { color: '#666666' },
        caption: { color: '#666666' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-filled': {
            backgroundColor: '#FF4444',
            color: 'white',
          },
          '&.MuiChip-outlined': {
            borderColor: '#FF4444',
            color: '#FF4444',
          },
        },
      },
    },
  },
});

const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  console.log('ProtectedRoute: Check', { currentUser: !!currentUser, loading });

  if (loading) {
    console.log('ProtectedRoute: Showing loading screen');
    return <LoadingScreen />;
  }

  if (!currentUser) {
    console.log('ProtectedRoute: No user, redirecting to /login');
    return <Navigate to="/login" replace />;
  }

  console.log('ProtectedRoute: User authenticated, showing protected content');
  return children;
};

// 관리자 전용 라우트 - 로그인한 모든 사용자에게 권한 부여
const AdminRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // 로그인한 모든 사용자에게 접근 권한 부여

  return children;
};

const PublicRoute = ({ children, allowAuthenticated = false }) => {
  const { currentUser, loading, isAdmin } = useAuth();

  console.log('PublicRoute: Check', { currentUser: !!currentUser, loading, allowAuthenticated });

  if (loading) {
    console.log('PublicRoute: Showing loading screen');
    return <LoadingScreen />;
  }

  // If allowAuthenticated is true, show the page even if user is logged in
  // This is useful for login selection pages where users might want to switch accounts
  if (allowAuthenticated) {
    console.log('PublicRoute: Allowing authenticated user to see page');
    return children;
  }

  // If user is authenticated, redirect to appropriate dashboard
  if (currentUser) {
    if (isAdmin && isAdmin()) {
      console.log('PublicRoute: Admin user authenticated, redirecting to /admin');
      return <Navigate to="/admin" replace />;
    }
    // For non-admin users (shouldn't happen in this app, but just in case)
    console.log('PublicRoute: Non-admin user authenticated, redirecting to /admin');
    return <Navigate to="/admin" replace />;
  }

  console.log('PublicRoute: No user, showing public content (login page)');
  return children;
};

// Root redirect component
const RootRedirect = () => {
  const { currentUser, loading, isAdmin } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  // If user is authenticated, redirect to appropriate dashboard
  if (currentUser && isAdmin && isAdmin()) {
    console.log('RootRedirect: Admin user detected, redirecting to /admin');
    return <Navigate to="/admin" replace />;
  }

  // If not authenticated, redirect to login
  console.log('RootRedirect: No authenticated user, redirecting to /admin/login');
  return <Navigate to="/admin/login" replace />;
};

function AppContent() {
  const { getRouteType } = useAuth();
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Routes>
        {/* Public Routes - Only admin login */}
        <Route
          path="/admin/login"
          element={
            <PublicRoute>
              <AdminLoginPage />
            </PublicRoute>
          }
        />
        
        {/* Dynamic root redirect based on auth status */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Navigate to="/admin/login" replace />} />
        
        
        {/* Admin Routes */}
        <Route
          path="/admin/*"
          element={
            <AdminRoute>
              <GlobalTenantProvider>
                <Layout isAdmin={true}>
                  <Routes>
                    <Route path="/" element={<TenantManagementPageOntology />} />
                    <Route path="/admin-management" element={<AdminManagementPage />} />
                    <Route path="/tenants" element={<TenantManagementPageOntology />} />

                    {/* 파일 온톨로지 */}
                    <Route path="/ontology" element={<OntologyPage />} />
                    <Route path="/prompt-management" element={<PromptManagementPage />} />

                    {/* 데이터 변환 */}
                    <Route path="/hyperlounge-converter" element={<HyperloungeConverterPage />} />
                    <Route path="/agent-monitor" element={<AgentMonitorPage />} />

                    <Route path="*" element={<Navigate to="/admin" replace />} />
                  </Routes>
                </Layout>
              </GlobalTenantProvider>
            </AdminRoute>
          }
        />
        
        {/* Catch all other routes */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Box>
  );
}

function App() {
  console.log('App.js: App component rendering');

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <AppContent />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  theme: {
                    primary: '#4caf50',
                  },
                },
                error: {
                  duration: 5000,
                  theme: {
                    primary: '#f44336',
                  },
                },
              }}
          />
        </Router>
      </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;